import React, { useState, useRef, useEffect } from 'react';
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import { TextField } from '@rmwc/textfield';
import '@rmwc/textfield/styles';
import { Button } from '@rmwc/button';
import '@rmwc/button/styles';
import './Popup.css';
import { useFlexionStore } from './Stores';
import { getText } from './Helpers';

const Werkstoff = {
  1: {
    E: 70000,
    G: 28000,
    Rp: 200,
  },
  2: {
    E: 210000,
    G: 80000,
    Rp: 250,
  },
};

let DummyID = 2000000000;

function ControlledPopup(props) {
  // Zustand store
  const setDummyData = useFlexionStore((state) => state.setDummyData);
  const Profiles = useFlexionStore((state) => state.Profiles);
  const setProfiles = useFlexionStore((state) => state.setProfiles);
  const Serie = useFlexionStore((state) => state.Serie);
  const setSerie = useFlexionStore((state) => state.setSerie);
  const Sprache = useFlexionStore((state) => state.Sprache);
  // state
  const [Profil, setProfil] = useState('');
  const [Material, setMaterial] = useState('');
  const [Breite, setBreite] = useState('');
  const [Höhe, setHöhe] = useState('');
  const [Ix, setIx] = useState('');
  const [Iy, setIy] = useState('');
  const [Lineargewicht, setLineargewicht] = useState('');
  const [It, setIt] = useState('');
  const [text, setText] = useState(getText(Sprache));

  // refs
  const ProfilRef = useRef(null);
  const MaterialRef = useRef(null);
  const LineargewichtRef = useRef(null);
  const BreiteRef = useRef(null);
  const HöheRef = useRef(null);
  const IxRef = useRef(null);
  const IyRef = useRef(null);

  useEffect(() => {
    setText(getText(Sprache));
  }, [Sprache]);

  /**
   * Alle Eingaben des Formulars zurücksetzen.
   */
  function resetFormState() {
    setProfil('');
    setMaterial('');
    setLineargewicht('');
    setBreite('');
    setHöhe('');
    setIx('');
    setIy('');
    setIt('');
  }

  /**
   * Überprüft ob das übergebene Element leer ist.
   * @param {Element} field Element das überprüft werden soll
   * @returns {boolean} true wenn Feld leer ist, false wenn Feld nicht leer ist
   */
  function isFieldEmpty(field) {
    return field.validity.valueMissing;
  }

  /**
   * Überprüft ob das übergebene Element das vorhandene pattern erfüllt
   * @param {Element} field Element das überprüft werden soll
   * @returns {boolean} true wenn pattern nicht übereinstimmt, false wenn pattern übereinstimmt
   */
  function isPatternMismatch(field) {
    return field.validity.patternMismatch;
  }

  /**
   * Setzt die übergebene Fehlermeldung bei einem Formfeld
   * @param {Element} field Element bei dem der Fehler gesetzt wird
   * @param {string} message Fehlermeldung
   */
  function setErrorMessage(field, message) {
    field.setCustomValidity(message);
  }

  /**
   * Entfernt Fehlermeldung bei einem Form Element
   * @param {Element} field Element bei dem die Fehlermeldung entfernt werden soll
   */
  function clearErrorMessage(field) {
    field.setCustomValidity('');
  }

  function changeProfil(e) {
    setProfil(e.target.value);
    if (isFieldEmpty(e.target)) setErrorMessage(e.target, text.Popup.EnterName);
    else clearErrorMessage(e.target);
  }

  function changeMaterial(e) {
    setMaterial(e.target.value);
    clearErrorMessage(e.target);
  }

  function changeBreite(e) {
    setBreite(e.target.value);
    if (isFieldEmpty(e.target)) setErrorMessage(e.target, text.Popup.EnterWidth);
    else if (isPatternMismatch(e.target)) setErrorMessage(e.target, text.Popup.EnterNumber);
    else clearErrorMessage(e.target);
  }

  function changeHöhe(e) {
    setHöhe(e.target.value);
    if (isFieldEmpty(e.target)) setErrorMessage(e.target, text.Popup.EnterHeight);
    else if (isPatternMismatch(e.target)) setErrorMessage(e.target, text.Popup.EnterNumber);
    else clearErrorMessage(e.target);
  }

  function changeIx(e) {
    setIx(e.target.value);
    if (isFieldEmpty(e.target)) setErrorMessage(e.target, text.Popup.EnterIx);
    else if (isPatternMismatch(e.target)) setErrorMessage(e.target, text.Popup.EnterNumber);
    else clearErrorMessage(e.target);
  }

  function changeIy(e) {
    setIy(e.target.value);
    if (isFieldEmpty(e.target)) setErrorMessage(e.target, text.Popup.EnterIy);
    else if (isPatternMismatch(e.target)) setErrorMessage(e.target, text.Popup.EnterNumber);
    else clearErrorMessage(e.target);
  }

  function changeIt(e) {
    setIt(e.target.value);
    if (isPatternMismatch(e.target)) setErrorMessage(e.target, text.Popup.EnterNumber);
    else clearErrorMessage(e.target);
  }

  function changeLineargewicht(e) {
    setLineargewicht(e.target.value);
    if (isFieldEmpty(e.target)) setErrorMessage(e.target, text.Popup.EnterLinear);
    else if (isPatternMismatch(e.target)) setErrorMessage(e.target, text.Popup.EnterNumber);
    else clearErrorMessage(e.target);
  }

  function validateAllInputsNotEmpty() {
    let valid = true;
    if (isFieldEmpty(ProfilRef.current)) {
      valid = false;
      setErrorMessage(ProfilRef.current, text.Popup.EnterName);
    }
    if (Material === '') {
      valid = false;
      setErrorMessage(MaterialRef.current, text.Popup.EnterMaterial);
    }
    if (isFieldEmpty(LineargewichtRef.current)) {
      valid = false;
      setErrorMessage(LineargewichtRef.current, text.Popup.EnterLinear);
    }
    if (isFieldEmpty(BreiteRef.current)) {
      valid = false;
      setErrorMessage(BreiteRef.current, text.Popup.EnterWidth);
    }
    if (isFieldEmpty(HöheRef.current)) {
      valid = false;
      setErrorMessage(HöheRef.current, text.Popup.EnterHeight);
    }
    if (isFieldEmpty(IxRef.current)) {
      valid = false;
      setErrorMessage(IxRef.current, text.Popup.EnterIx);
    }
    if (isFieldEmpty(IyRef.current)) {
      valid = false;
      setErrorMessage(IyRef.current, text.Popup.EnterIy);
    }
    return valid;
  }

  function handleSubmitButton(e) {
    validateAllInputsNotEmpty();
  }

  function handleFormSubmit(e) {
    e.preventDefault();
    createDummy(DummyID);
    DummyID += 1;
    closePopup();
  }

  function closePopup() {
    props.closepop();
    resetFormState();
  }

  function createDummy(id) {
    let Querschnitt;
    if (Breite === Höhe) Querschnitt = 1;
    else Querschnitt = 2;
    const dummyData = {
      ID: id,
      Series: 'Dummy',
      Description: Profil,
      Picture: false,
      X: Breite,
      Y: Höhe,
      Fläche: '',
      Ix,
      Iy,
      Wx: '',
      Wy: '',
      Lineargewicht,
      E: Werkstoff[Material].E,
      'Rp0,2': Werkstoff[Material].Rp,
      Querschnitt,
      It,
      G: Werkstoff[Material].G,
    };

    setDummyData(dummyData);

    const newProfile = {
      ID: id,
      Series: 'Dummy',
      Description: Profil,
    };

    const ProfilesArray = Array.from(Profiles);
    ProfilesArray.push(newProfile);
    setProfiles(ProfilesArray);

    if (!Serie.includes('Dummy')) {
      const SerieArray = Array.from(Serie);
      SerieArray.push('Dummy');
      setSerie(SerieArray);
    }
  }

  const PopupInhalt = (
    <>
      <form onSubmit={handleFormSubmit}>
        <div className='Profile'>
          <div className='DummyProfilPop'>
            {text.Popup ? text.Popup.Name : ''} :{' '}
            <TextField
              required
              name='Profil'
              label={text.Popup ? text.Popup.NameLabel : ''}
              onChange={changeProfil}
              value={Profil}
              ref={ProfilRef}
            />
          </div>
        </div>
        <div className='ProfileData'>
          <div className='MaterialWrapper' >
            {text.Popup ? text.Popup.Material : ''} :{' '}
            <select
              className='MaterialSelect'
              required
              label={text.Popup ? text.Popup.Material : ''}
              placeholder={text.Popup ? text.Popup.Material : ''}
              onChange={changeMaterial}
              value={Material}
              ref={MaterialRef}
            >
              <option value='' disabled>
                {text.Popup.Material}
              </option>
              <option value='1'>{text.Popup.Alu}</option>
              <option value='2'>{text.Popup.Steel}</option>
            </select>
          </div>
          <div className='DummyLineargewicht'>
            {text.Popup ? text.Popup.Linear : ''} :{' '}
            <TextField
              required
              name='Lineargewicht'
              label={text.Popup ? text.Popup.LinearLabel : ''}
              onChange={changeLineargewicht}
              value={Lineargewicht}
              ref={LineargewichtRef}
              pattern='^[0-9]+\.?[0-9]*$'
            />
          </div>
          <div className='DummyBreite'>
            {text.Popup ? text.Popup.Width : ''}:{' '}
            <TextField
              required
              name='Breite'
              label={text.Popup ? text.Popup.WidthLabel : ''}
              onChange={changeBreite}
              value={Breite}
              ref={BreiteRef}
              pattern='^[0-9]+\.?[0-9]*$'
            />
          </div>
          <div className='DummyHöhe'>
            {text.Popup ? text.Popup.Height : ''} :{' '}
            <TextField
              required
              name='Höhe'
              label={text.Popup ? text.Popup.HeightLabel : ''}
              onChange={changeHöhe}
              value={Höhe}
              ref={HöheRef}
              pattern='^[0-9]+\.?[0-9]*$'
            />
          </div>
          <div className='DummyIx'>
            Ix* :{' '}
            <TextField
              required
              name='Ix'
              label={text.Popup ? text.Popup.IxLabel : ''}
              onChange={changeIx}
              value={Ix}
              ref={IxRef}
              pattern='^[0-9]+\.?[0-9]*$'
            />
          </div>
          <div className='DummyIy'>
            Iy* :{' '}
            <TextField
              required
              name='Iy'
              label={text.Popup ? text.Popup.IyLabel : ''}
              onChange={changeIy}
              value={Iy}
              ref={IyRef}
              pattern='^[0-9]+\.?[0-9]*$'
            />
          </div>
          <div className='DummyIt'>
            It :{' '}
            <TextField
              pattern='^[0-9]+\.?[0-9]*$'
              name='It'
              onChange={changeIt}
              value={It}
              label={text.Popup ? text.Popup.ItLabel : ''}
            />
          </div>
        </div>
        <Button label={text.Popup ? text.Popup.ButtonLabel : ''} onClick={handleSubmitButton} />
      </form>
    </>
  );

  return (
    <div>
      <Popup open={props.open} className='DummyModal' modal closeOnDocumentClick onClose={closePopup}>
        <div className='Modal'>{PopupInhalt}</div>
      </Popup>
    </div>
  );
}

export default ControlledPopup;
